import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Loader2, Store, FileText, Smartphone } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { platformApi } from '@/services/platformApi';
import CreateCashToken from '@/components/CreateCashToken';
import type { Token } from '@/components/TokenCard';
import {
  type ParsePoSResult,
  type VaiuPosBasketV1,
  basketSummaryMessage,
  parseVaiuPosIngress,
  tryParseVaiuPosFromLocationSearch,
} from '@/utils/posExchange';
import { nfcBridgeService } from '@/services/nfcBridgeService';
import { CurrencyDisplay } from '@vaiu/ui';
import { i18n } from '@/utils/i18n';

function basketToExtraPayload(b: VaiuPosBasketV1): Record<string, unknown> {
  const o: Record<string, unknown> = {
    posSource: 'vaiu_pos',
  };
  if (b.posSessionId) o.posSessionId = b.posSessionId;
  if (b.basket?.length) o.basket = b.basket;
  if (b.signature) o.signature = b.signature;
  return o;
}

/**
 * PoS checkout entry: `/pos?data=...` or `?type=invoice|basket&...`
 * NFC shell may dispatch `vaiu-pos-context` or postMessage `{ type: 'vaiu-pos-context', raw }`.
 */
const PosCheckoutPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();

  const [ingress, setIngress] = useState<ParsePoSResult | null>(null);
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [createdTokenKey, setCreatedTokenKey] = useState<string | null>(null);
  const [writeState, setWriteState] = useState<'idle' | 'pending' | 'ok' | 'err'>('idle');
  const [writeError, setWriteError] = useState<string | null>(null);

  const applyRaw = useCallback((raw: string) => {
    setIngress(parseVaiuPosIngress(raw));
    setCreatedTokenKey(null);
    setWriteState('idle');
    setWriteError(null);
  }, []);

  useEffect(() => {
    const fromUrl = tryParseVaiuPosFromLocationSearch(location.search);
    if (fromUrl !== null) {
      setIngress(fromUrl);
      setCreatedTokenKey(null);
      setWriteState('idle');
      setWriteError(null);
    }
  }, [location.search]);

  useEffect(() => {
    const onCustom = (e: Event) => {
      const ce = e as CustomEvent<{ raw?: string }>;
      const raw = ce.detail?.raw;
      if (typeof raw === 'string' && raw.trim()) applyRaw(raw);
    };
    window.addEventListener('vaiu-pos-context', onCustom as EventListener);
    return () => window.removeEventListener('vaiu-pos-context', onCustom as EventListener);
  }, [applyRaw]);

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data;
      if (d && typeof d === 'object' && d.type === 'vaiu-pos-context' && typeof d.raw === 'string') {
        applyRaw(d.raw);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [applyRaw]);

  useEffect(() => {
    if (isGuest || !user) {
      setBalances({});
      setBalancesLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const b = await platformApi.getBalances();
        if (!cancelled) setBalances(b || {});
      } catch {
        if (!cancelled) setBalances(user.balances || {});
      } finally {
        if (!cancelled) setBalancesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isGuest, user]);

  const basketPayload = useMemo(() => {
    if (!ingress?.ok) return null;
    return ingress.payload.type === 'basket' ? ingress.payload : null;
  }, [ingress]);

  /** Wire key from basket (e.g. MM$<issuerId> for MeMoney) — must match `user.balances` keys. */
  const selectedCurrency = basketPayload?.currency || user?.currency || 'USD';

  const extraTokenPayload = useMemo(() => {
    if (!basketPayload) return undefined;
    return basketToExtraPayload(basketPayload);
  }, [basketPayload]);

  const handleInvoiceContinue = () => {
    if (!ingress?.ok || ingress.payload.type !== 'invoice') return;
    navigate(`/invoice/${encodeURIComponent(ingress.payload.invoiceTokenKey)}`);
  };

  const handleTokenCreated = (_token: Token) => {
    setCreatedTokenKey(_token.key);
    setWriteState('idle');
    setWriteError(null);
  };

  const handleWriteToPoS = async () => {
    if (!createdTokenKey) return;
    setWriteState('pending');
    setWriteError(null);
    const res = await nfcBridgeService.writePoSResponse(createdTokenKey, 'text');
    if (res.success) {
      setWriteState('ok');
    } else {
      setWriteState('err');
      setWriteError(res.error || 'write_failed');
    }
  };

  if (balancesLoading && user && !isGuest) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>{i18n.__('common.loading')}</span>
      </div>
    );
  }

  if (!ingress) {
    return (
      <div className="mx-auto max-w-lg p-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              PoS checkout
            </CardTitle>
            <CardDescription>
              Waiting for checkout data from the terminal or link. Open this page from a PoS QR/deep link, or tap the
              terminal with your phone in the VAIU NFC app.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                void nfcBridgeService.readPoSPayload().then((r) => {
                  if (r.success && r.payload) applyRaw(r.payload);
                })
              }
            >
              <Smartphone className="mr-2 h-4 w-4" />
              Read from NFC
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!ingress.ok) {
    return (
      <div className="mx-auto max-w-lg p-4">
        <Alert variant="destructive">
          <AlertDescription>
            Invalid PoS data: {ingress.error}. Expected JSON v1 (invoice or basket) per VAIU-POS-EXCHANGE.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (ingress.payload.type === 'invoice') {
    const p = ingress.payload;
    return (
      <div className="mx-auto max-w-lg p-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Pay invoice
            </CardTitle>
            <CardDescription>PoS sent an invoice checkout. Continue to review and pay.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {p.sessionId ? (
              <p className="text-sm text-muted-foreground">
                Session: <span className="font-mono">{p.sessionId}</span>
              </p>
            ) : null}
            <Button type="button" className="w-full" onClick={handleInvoiceContinue}>
              Continue to invoice payment
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const b = ingress.payload;

  return (
    <div className="mx-auto max-w-lg p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            PoS basket
          </CardTitle>
          <CardDescription>Create a cash token for this checkout. Amount and details come from the terminal.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="text-lg font-semibold">
            <CurrencyDisplay amount={b.amount} currency={b.currency} />
          </div>
          {b.posSessionId ? (
            <p className="text-sm text-muted-foreground">
              PoS session: <span className="font-mono">{b.posSessionId}</span>
            </p>
          ) : null}
          {b.basket?.length ? (
            <ul className="text-sm border rounded-md divide-y max-h-40 overflow-y-auto">
              {b.basket.map((line, i) => (
                <li key={i} className="px-3 py-2 flex justify-between gap-2">
                  <span>
                    {line.name} × {line.qty}
                  </span>
                  <span className="text-muted-foreground">{line.unitPrice}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </CardContent>
      </Card>

      <CreateCashToken
        typeOfToken="cash"
        selectedCurrency={selectedCurrency}
        balances={balances}
        allowCurrencyChange={false}
        initialAmount={String(b.amount)}
        initialMessage={basketSummaryMessage(b)}
        merchantId={b.merchantId}
        isGuest={isGuest}
        showBalanceInfo
        showTypeSlider={false}
        extraTokenPayload={extraTokenPayload}
        onCancel={() => navigate(-1)}
        onSubmit={handleTokenCreated}
      />

      {createdTokenKey ? (
        <Card className="border-primary/30">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              Return token to PoS
            </CardTitle>
            <CardDescription>
              If your store terminal expects an NFC handoff, hold your phone to the PoS to write the token key.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs font-mono break-all text-muted-foreground">{createdTokenKey}</p>
            <Button type="button" onClick={() => void handleWriteToPoS()} disabled={writeState === 'pending'}>
              {writeState === 'pending' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Writing…
                </>
              ) : (
                'Hold phone to terminal to complete'
              )}
            </Button>
            {writeState === 'ok' ? (
              <Alert>
                <AlertDescription>Token written to the terminal.</AlertDescription>
              </Alert>
            ) : null}
            {writeState === 'err' && writeError ? (
              <Alert variant="destructive">
                <AlertDescription>{writeError}</AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default PosCheckoutPage;
